import { FC, ReactNode, useRef, useCallback, useEffect, useState } from 'react';
import { CircularProgress, Box, Select, MenuItem, FormControl, InputLabel, Button } from '@mui/material';
import { orderBy, SortDescriptor } from '@progress/kendo-data-query';
import { Grid, GridColumn, GridSortChangeEvent } from '@progress/kendo-react-grid';
import { ExcelExport } from '@progress/kendo-react-excel-export'; // Import ExcelExport
import VendorApi from 'src/api/VendorApi';
import useVendor from 'src/hooks/useVendor';

import { IShowTotalsReport } from 'src/types/IShowTotalsReport';

declare interface ShowTotalsProps {
  children?: ReactNode;
}

const ShowTotalsReport: FC<ShowTotalsProps> = () => {
  const { vendor } = useVendor();
  const [isLoading, setIsLoading] = useState(false);
  const [sort, setSort] = useState<Array<SortDescriptor>>([{ field: 'fieldName', dir: 'asc' }]);
  const [data, setData] = useState<IShowTotalsReport[]>([]);
  const excelExportRef = useRef<ExcelExport | null>(null); // Ref for ExcelExport
  const gridRef = useRef(null);

  // New state for selected year
  const [selectedYear, setSelectedYear] = useState(new Date().getFullYear());
  const years = Array.from(new Array(6), (val, index) => new Date().getFullYear() - index);

  const loadData = useCallback(() => {
    setIsLoading(true);
    VendorApi.getVendorShowTotals(vendor.number, selectedYear)
      .then((result) => {
        setData(result);
      })
      .catch((err) => {
        console.error(err);
      })
      .finally(() => {
        setIsLoading(false);
      });
  }, [vendor?.number, selectedYear]);

  useEffect(() => {
    loadData();
  }, [vendor?.number, loadData, selectedYear]);

  const handleExport = () => {
    if (excelExportRef.current) {
      excelExportRef.current.save();
    }
  };

  return (
    <>
      <Box sx={{ display: 'flex', flexDirection: 'column', overflowX: 'auto', position: 'relative' }}>
        <FormControl sx={{ width: 125, marginTop: 2, marginBottom: 2 }}>
          <InputLabel id="year-label">Year</InputLabel>
          <Select labelId="year-label" label="Year" value={selectedYear} onChange={(e) => setSelectedYear(e.target.value as number)}>
            {years.map((year) => (
              <MenuItem key={year} value={year}>
                {year}
              </MenuItem>
            ))}
          </Select>
        </FormControl>

        <Button onClick={handleExport} variant="contained" sx={{ width: 200, marginBottom: 2 }}>
          Export to Excel
        </Button>

        {isLoading && (
          <CircularProgress sx={{ position: 'absolute', mt: 1, top: '50%', left: '50%', transform: 'translate(-50%, -50%)', zIndex: 9 }} size="1.5rem"></CircularProgress>
        )}

        <ExcelExport data={data} ref={excelExportRef}>
          <Grid
            ref={gridRef}
            data={orderBy(data, sort)}
            total={data.length}
            sort={sort}
            id="showTotalsReportGrid"
            sortable={true}
            onSortChange={(e: GridSortChangeEvent) => {
              setSort(e.sort);
            }}
            resizable={true}
            style={{ width: '1300px', maxHeight: '500px', overflowY: 'auto' }}
          >
            <GridColumn field="vendorNumber" title="Vendor Number" width={140} />
            <GridColumn field="programGroupId" title="Program Group ID" width={150} />
            <GridColumn field="programCode" title="Program Code" width={350} />
            <GridColumn field="year" title="Year" width={75} />
            <GridColumn field="showStartDate" title="Show Start Date" width={150} />
            <GridColumn field="show" title="Show" width={275} />
            <GridColumn field="total" title="Total" width={200} />
          </Grid>
        </ExcelExport>
      </Box>
    </>
  );
};

export default ShowTotalsReport;
